import React from "react";

import orderBy from "lodash.orderby";
import sortBy from "lodash.sortby";
import GetText from "../../i18n/GetText";
import {
  Level,
  SanityCountry,
  SanityGlobalRegion,
  SanityRegion
} from "../../model/common";
import { SanityDybProject } from "../../model/impact";
import { SanityUnitOfWork, subjectsFromTopics } from "../../model/lessons";
import { SanityMake } from "../../model/makes";
import { SanityResourcePage } from "../../model/teach";
import { levelToNumber } from "../../utils/level-util";
import { getUnitTopics } from "../../utils/units-util";
import Filter from "../Filter/Filter/Filter";
import RadioRowFilter from "../Filter/RadioRowFilter/RadioRowFilter";
import SubjectTopicFilterTree from "../FilterTree/SubjectTopicFilterTree";
import LevelIndicator from "../LevelIndicator/LevelIndicator";
import { FilterConfigItem, FilterType, ListingConfig } from "./filters";
import { getYearFromDate } from "../../utils/dates";
import LocationFilterTree from "../FilterTree/LocationFilterTree";

export const ageFilter: FilterConfigItem<SanityUnitOfWork> = {
  id: "age",
  titleId: "age",
  location: "top",
  values: (item: SanityUnitOfWork) =>
    item.ageRange.map(ageRange => ({
      _id: ageRange,
      name: ageRange
    })),
  // Single age range filter allowed, so permit if any age range in the unit of work matches.
  filter: (values, data) =>
    data.filter(unit => unit.ageRange.some(a => values.some(v => a === v._id))),
  component: ({ filterData, ...others }) => {
    const sortedValues = [...filterData].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    return (
      <RadioRowFilter
        key="age"
        allTextId="all-age-groups"
        renderValue={({ value }) => <GetText id={value._id} />}
        filterData={sortedValues}
        {...others}
      />
    );
  }
};

export const levelFilter: FilterConfigItem<SanityMake> = {
  id: "level",
  titleId: "level",
  location: "top",
  values: (item: SanityMake) => [
    {
      _id: item.level,
      name: item.level
    }
  ],
  filter: (values, data) =>
    data.filter(unit => values.some(v => unit.level === v._id)),
  component: ({ filterData, ...others }) => {
    const sortedValues = sortBy(filterData, x => levelToNumber(x._id as Level));
    return (
      <RadioRowFilter
        key="level"
        allTextId="all-levels"
        renderValue={({ value }) => (
          <LevelIndicator value={value._id as Level} />
        )}
        filterData={sortedValues}
        {...others}
      />
    );
  }
};

export const programmingLanguageFilter: FilterConfigItem<
  SanityUnitOfWork | SanityMake
> = {
  id: "programmingLanguage",
  titleId: "programming-language",
  location: "side",
  values: item =>
    item.programmingLanguages.map(programmingLanguage => ({
      _id: programmingLanguage,
      name: programmingLanguage
    })),
  // Multiple programming languages can be selected, we require the unit of work to support all of them
  filter: (values, data) =>
    data.filter(unit =>
      values.every(v => unit.programmingLanguages.some(p => v._id === p))
    ),
  component: Filter
};

export const unitTypeFilter: FilterConfigItem<SanityUnitOfWork> = {
  id: "unitType",
  titleId: "unitType",
  location: "side",
  values: item => [
    {
      _id: item.unitType,
      name: `unitType-${item.unitType}`
    }
  ],
  filter: (values, data) =>
    data.filter(unit => values.some(v => v._id === unit.unitType)),
  component: Filter
};

export const resourceTypeFilter: FilterConfigItem<SanityResourcePage> = {
  id: "resourceType",
  titleId: "type",
  location: "side",
  values: item =>
    item.tags.map(tag => ({
      _id: tag._id,
      name: tag.name
    })),
  // Multiple tags can be selected, we require the resource to have all of them
  filter: (values, data) =>
    data.filter(resource =>
      values.every(v => resource.tags.some(p => v._id === p._id))
    ),
  component: Filter
};

const getTopics = (value: SanityUnitOfWork | SanityMake) => {
  if (value._type === "unitOfWork") {
    // Need to aggregate for new-style units.
    return getUnitTopics(value as SanityUnitOfWork);
  }
  return value.topics;
};

export const topicFilter: FilterConfigItem<SanityUnitOfWork | SanityMake> = {
  id: "subjectsAndTopics",
  titleId: "subjects-and-topics",
  location: "side",
  values: item => {
    const topics = getTopics(item);
    const subjects = subjectsFromTopics(topics);
    return [...topics, ...subjects];
  },
  // Multiple subjects or topics can be selected, we require the unit of work to have all of them.
  // It can have them at the unit level OR on any lesson.
  filter: (values, data) => {
    const topics = values.filter(v => !!v.subject);
    const subjects = values.filter(v => !v.subject);
    return data
      .filter(unit => {
        return topics.every(v => getTopics(unit).some(t => v._id === t._id));
      })
      .filter(unit =>
        subjects.every(v => {
          return getTopics(unit)
            .map(t => t.subject)
            .some(t => v._id === t._id);
        })
      );
  },
  component: SubjectTopicFilterTree
};

export const deviceFeatureFilter: FilterConfigItem<SanityMake> = {
  id: "deviceFeature",
  titleId: "device-feature",
  location: "side",
  values: item => item.deviceFeatures,
  // Multiple device features can be selected, we require the make to support all of them
  filter: (values, data) =>
    data.filter(make =>
      values.every(v => make.deviceFeatures.some(f => v._id === f._id))
    ),
  component: Filter
};

export const unitOfWorkFilters: ListingConfig<any> = {
  filters: [ageFilter, unitTypeFilter, programmingLanguageFilter, topicFilter],
  searchFields: (v: SanityUnitOfWork) => unitFields(v)
};

export const makeFilters: ListingConfig<any> = {
  filters: [
    levelFilter,
    programmingLanguageFilter,
    topicFilter,
    deviceFeatureFilter
  ],
  searchFields: (v: SanityMake) => [v.title, v.description]
};

export const resourceFilters: ListingConfig<any> = {
  filters: [resourceTypeFilter],
  searchFields: (v: SanityResourcePage) => [v.title]
};

const unitFields = (v: SanityUnitOfWork) => [
  v.title,
  v.description,
  ...v.contents.map(l => l.title)
];

export const globalGoalsFilter: FilterConfigItem<SanityDybProject> = {
  id: "globalGoals",
  titleId: "global-goals",
  location: "side",
  values: item => {
    return item.globalGoals;
  },
  filter: (values, data) =>
    data.filter(p =>
      values.some(v => p.globalGoals.some(g => v._id === g._id))
    ),
  component: filterProps => {
    const sortFn = (data: FilterType[]) => orderBy(data, ["number"], ["asc"]);
    return <Filter {...filterProps} sortFn={sortFn} filterType="inclusive" />;
  }
};

export const submissionYearFilter: FilterConfigItem<SanityDybProject> = {
  id: "submissionYear",
  titleId: "year",
  location: "side",
  values: item => {
    const year = getYearFromDate(item.date);
    return [
      {
        _id: year,
        name: year
      }
    ];
  },
  filter: (values, data) =>
    data.filter(p => values.map(v => v.name).includes(getYearFromDate(p.date))),
  component: filterProps => {
    const sortFn = (data: FilterType[]) => orderBy(data, ["name"], ["desc"]);
    return <Filter {...filterProps} sortFn={sortFn} filterType="inclusive" />;
  }
};

export const locationFilter: FilterConfigItem<SanityDybProject> = {
  id: "location",
  titleId: "location",
  location: "side",
  values: item => {
    const values = [
      item.location,
      (item.location as SanityCountry)?.globalRegion
    ].filter(Boolean) as (SanityRegion | SanityCountry | SanityGlobalRegion)[];
    return values;
  },
  filter: (values, data) => {
    const countries = values.filter(v => v._type === "country");
    const globalRegions = values.filter(v => v._type === "globalRegion");
    return data.filter(project => {
      return (
        countries.some(v => project.location._id === v._id) ||
        globalRegions.some(
          v => (project.location as SanityCountry)?.globalRegion?._id === v._id
        )
      );
    });
  },
  component: LocationFilterTree
};

export const dybProjectFilters: ListingConfig<any> = {
  filters: [submissionYearFilter, locationFilter, globalGoalsFilter],
  // No one has specified what fields to search yet
  searchFields: (v: SanityDybProject) => [v.title, v.subtitle]
};
